// src/modules/app-views/_apps/appsindico/parkingSpots/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import ParkingSpotsInit from './pages/Init';
import ListProvider from 'components/core/ListProvider';
import DocumentProvider from 'components/core/DocumentProvider';
import ParkingSpotsDocument from './pages/Document';
import parkingSpotsInitActionButton from './pages/Init/actions/actionButton';
import parkingSpotsDocumentActionButton from './pages/Document/actions/actionButton';
import parkingSpotsDocumentDecorateRequest from './pages/Document/decorators/request';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'parkingSpots';

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 2,
  id: MODULE_ID,
  initialPage: 'init',
  initialParams: {},
  initialQuery: {},
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  init: {
    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'list',
    moduleId: MODULE_ID,
    component: props => (
      <ListProvider {...props}>
        <ParkingSpotsInit />
      </ListProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: '/v1/parking-spots/pagination',
      params: null,
      query: null,

      // data transformation
      returnAsObject: false,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: null,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: null,
      paramKey: null,
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: {
      pagination: { page: 1, size: 10 },
      sort: [{ field: '_metadata.audit.updatedAt', order: 'desc', index: 1 }],
      search: {},
      textSearch: '',
      filter: {},
      drawer: { folder: 'documents' },
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: {
      actions: ['edit', 'archive', 'remove'],
    },

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: null,

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'default',
      actions: ['filter', 'refresh', parkingSpotsInitActionButton()],

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['management', 'parking_spots'],
      },
    },
  },

  document: {
    highSecurity: false,

    // ───────────────────────────────────────────────────────────────────────
    // PAGE CORE
    // ───────────────────────────────────────────────────────────────────────
    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <ParkingSpotsDocument />
      </DocumentProvider>
    ),

    // ───────────────────────────────────────────────────────────────────────
    // TOUR
    // ───────────────────────────────────────────────────────────────────────
    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: {
        br: null,
        us: null,
      },
    },

    // ───────────────────────────────────────────────────────────────────────
    // API & DATA
    // ───────────────────────────────────────────────────────────────────────
    api: {
      url: '/v1/parking-spots',
      params: null,
      query: null,

      // data transformation
      returnAsObject: true,

      // initial configuration
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    // ───────────────────────────────────────────────────────────────────────
    // DECORATORS
    // ───────────────────────────────────────────────────────────────────────
    decorators: {
      request: parkingSpotsDocumentDecorateRequest,
      response: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // ROUTING & NAVIGATION
    // ───────────────────────────────────────────────────────────────────────
    routing: {
      endpoint: '/:_id',
      paramKey: '_id',
      query: null,
    },

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - PAGINATION CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    pagination: null,

    // ───────────────────────────────────────────────────────────────────────
    // LIST PROVIDER ONLY - TABLE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    table: null,

    // ───────────────────────────────────────────────────────────────────────
    // DOCUMENT PROVIDER ONLY - PAGE SETTINGS
    // ───────────────────────────────────────────────────────────────────────
    settings: {
      initialTab: 'record',
      initialValues: {
        status: 'available',
        type: 'covered',
      },
      actions: ['editRefresh', parkingSpotsDocumentActionButton(), 'return'],
      viewMode: 'auto',
    },

    // ───────────────────────────────────────────────────────────────────────
    // HEADER CONFIGURATION
    // ───────────────────────────────────────────────────────────────────────
    header: {
      visible: true,
      style: 'card',

      avatar: {
        visible: false,
      },

      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['management', 'parking_spots'],
      },

      card: {
        title: () => null,
        subtitle: () => null,
        column1: () => null,
        column2: () => null,
      },
    },
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, preConfigChanges };
